import { vehicleApi } from '../features/vehicles/api';
import { userApi } from '../features/users/api';
import { controllerApi } from '../features/controllers/api';
import { societyApi } from '../features/societies/api';
import { inventoryApi } from '../features/inventory/api';
import { resolveControllerDetailPath } from '../features/controllers/controllerAccess';

const RESULT_LIMIT = 5;

// Pull the first array we can find from a list response, tolerating the
// several envelope shapes the backend uses (data, data.data, data.<key>).
const extractList = (response, key) => {
  const payload = response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (key && Array.isArray(payload[key])) return payload[key];
  if (key && Array.isArray(payload.data?.[key])) return payload.data[key];
  return [];
};

const societyNameOf = (entity) =>
  entity?.society?.name || entity?.allocated_society?.name || entity?.societyName || null;

// Builds the resource search providers consumed by GlobalSearch. `societyId`
// scopes the society-bound resources so a switchable admin searches within the
// active society; the societies provider stays global.
export const buildGlobalSearchProviders = ({ navigate, societyId, role }) => {
  const go = (path) => () => navigate?.(path);
  const scoped = societyId || undefined;

  return [
    {
      key: 'vehicles',
      label: 'Vehicles',
      run: async (query) => {
        const response = await vehicleApi.getVehicles({
          search: query,
          page: 1,
          limit: RESULT_LIMIT,
          societyId: scoped,
        });
        return extractList(response).map((vehicle) => ({
          id: vehicle.id || vehicle.vehicleNumber,
          title: vehicle.vehicleNumber || vehicle.registrationNumber || 'Vehicle',
          subtitle: vehicle.owner?.name || vehicle.ownerName || vehicle.tagId || '',
          scopeLabel: societyNameOf(vehicle),
          onOpen: go(`/vehicles?search=${encodeURIComponent(vehicle.vehicleNumber || '')}`),
        }));
      },
    },
    {
      key: 'users',
      label: 'Users',
      run: async (query) => {
        const response = await userApi.getUsers({
          q: query,
          search: query,
          page: 1,
          limit: RESULT_LIMIT,
          societyId: scoped,
        });
        return extractList(response, 'users').map((user) => ({
          id: user.id,
          title: user.fullName || user.name || 'User',
          subtitle: user.phone || user.mobile || user.email || '',
          scopeLabel: societyNameOf(user),
          onOpen: go(`/users/${encodeURIComponent(user.id)}`),
        }));
      },
    },
    {
      key: 'tags',
      label: 'RFID Tags',
      run: async (query) => {
        const response = await inventoryApi.getTags({
          search: query,
          q: query,
          page: 1,
          limit: RESULT_LIMIT,
          societyId: scoped,
        });
        return extractList(response, 'tags').map((tag) => ({
          id: tag.id || tag.tagNumber,
          title: tag.tagNumber || tag.tagId || 'Tag',
          subtitle: tag.status || '',
          scopeLabel: societyNameOf(tag),
          onOpen: go(`/inventory/global?search=${encodeURIComponent(tag.tagNumber || '')}`),
        }));
      },
    },
    {
      key: 'controllers',
      label: 'Controllers',
      run: async (query) => {
        const response = await controllerApi.getDevices({
          q: query,
          page: 1,
          limit: RESULT_LIMIT,
          societyId: scoped,
        });
        return extractList(response).map((device) => ({
          id: device.id,
          title: device.name || device.deviceId || 'Controller',
          subtitle: device.deviceId || '',
          scopeLabel: societyNameOf(device),
          onOpen: go(
            resolveControllerDetailPath(
              role,
              device.id,
              query ? `/controllers?q=${encodeURIComponent(query)}` : '/controllers',
            ),
          ),
        }));
      },
    },
    {
      key: 'societies',
      label: 'Societies',
      run: async (query) => {
        const response = await societyApi.getSocieties({
          search: query,
          q: query,
          page: 1,
          limit: RESULT_LIMIT,
        });
        return extractList(response).map((society) => ({
          id: society.id,
          title: society.name || 'Society',
          subtitle: society.city || society.place || '',
          scopeLabel: 'Global',
          onOpen: go(`/societies/${encodeURIComponent(society.id)}`),
        }));
      },
    },
  ];
};

export default buildGlobalSearchProviders;
